import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAuth } from '@/store/auth/auth.store';
import {
  useBilledServiceIdsQuery,
  useSubscriptionsQuery,
} from '@/store/subscriptions/subscriptions.store';
import type { Subscription } from '@/store/subscriptions/subscriptions.types';
import { getVesselsByIds } from '@/store/vessels/vessel.api';
import type { Vessel } from '@/store/vessels/vessel.dto';

import { planManageableServices } from './subscription-access';

export type ManagedVesselSubscription = {
  vessel: Vessel;
  /**
   * `null` when the vessel has no subscription row at all. To the user that reads the
   * same as a `harbor` row: the free plan.
   */
  subscription: Subscription | null;
  /**
   * Whether the plan is billed to this user. A vessel's row says what the plan is and never
   * whose card it is on, so this is read from their own bill instead — see
   * `getBilledServiceIds`. Unknown reads as `true`, which is the ordinary case and the one
   * that states nothing extra.
   */
  isPayer: boolean;
  /**
   * Whether this user may take the plan over from whoever is paying for it — the owner, a
   * co-owner, or a captain on a vessel with no owner — so they are the ones told where to do it.
   * The backend answers it as `can_take_over`; the `owner_id` check covers a backend that does
   * not send it yet.
   */
  canTakeOver: boolean;
};

/**
 * Every vessel whose plan the current user may manage, each joined to its subscription.
 *
 * Which vessels those are comes from their own role rows rather than from ownership: a
 * vessel's plan is not the owner's alone — a captain can be the one paying for a vessel that
 * has no owner yet, and is granted `GetSubscription` / `EditSubscription` for exactly that.
 * A list filtered by `owner_id` showed such a captain nothing at all. See
 * `subscription-access`.
 *
 * The two sides come from different services: the vessels from the vessel API by id, and
 * each vessel's plan from `GET /subscriptions` scoped to it. Per vessel rather than one
 * `/subscriptions/all` — see `useSubscriptionsQuery` for why that endpoint answers the wrong
 * question here, now that a listed vessel need not be one the caller is billed for.
 */
export const useManagedVesselSubscriptions = () => {
  // Read off the session rather than refetched: the role rows and their rights are loaded
  // when the session is, and refreshed by everything that changes them — creating a vessel,
  // accepting an invitation. Asking `/me` again here would also re-run `normalizeSession`,
  // which can move the active service out from under the page it was opened from.
  const session = useAuth((state) => state.session);
  const personId = session?.id;

  const serviceIds = useMemo(
    () => planManageableServices(session?.services).map((service) => service.service_id),
    [session?.services],
  );

  const vesselsQuery = useQuery({
    queryKey: ['plan-manageable-vessels', serviceIds],
    queryFn: () => getVesselsByIds(serviceIds),
    enabled: serviceIds.length > 0,
  });

  const subscriptions = useSubscriptionsQuery(serviceIds);
  const { byServiceId } = subscriptions;
  // One request for the whole list: which of these vessels are on this user's own card.
  const billed = useBilledServiceIdsQuery();

  const items = useMemo<ManagedVesselSubscription[]>(
    () =>
      (vesselsQuery.data ?? []).map((vessel) => ({
        vessel,
        subscription: byServiceId.get(vessel.id) ?? null,
        isPayer: billed.data?.has(vessel.id) ?? true,
        canTakeOver:
          !!byServiceId.get(vessel.id)?.canTakeOver || (!!personId && vessel.owner_id === personId),
      })),
    [vesselsQuery.data, byServiceId, billed.data, personId],
  );

  // Nothing to manage means nothing to ask for either: the vessels query stays disabled, so
  // it would report `isPending` forever and the list would spin.
  const hasNothingToManage = !!session && serviceIds.length === 0;

  return {
    items,
    // Vessels drive the rows; subscriptions only decorate them, so the list can render
    // as soon as the vessels land.
    isLoading: !hasNothingToManage && (!session || vesselsQuery.isPending),
    isSubscriptionsPending: !hasNothingToManage && (subscriptions.isPending || billed.isPending),
    /*
     * The bill is deliberately left out of this. It adds a line to a vessel rather than
     * deciding whether one can be drawn, and its fallback — the caller pays — is the ordinary
     * answer; failing the whole list over it would hide every vessel to protect one sentence.
     */
    error: vesselsQuery.error ?? subscriptions.error,
  };
};
